/** @jsxImportSource @emotion/react */
import { useEffect } from "react";
import {
  CreateMenuData,
  Menu,
  MenuInfo,
  UpdateMenuData,
} from "../models/Menus";
import { FormProvider, useForm } from "react-hook-form";
import { FormRow } from "../../common/hook-form";
import { getCountrySpecificValues } from "../../../util";
import { useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { routes } from "../../../appRoutingHelpers";
import { ButtonAction } from "../../../components/NewButtons";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { useCreateMenu, useUpdateMenu } from "../service/menus-api";
import { SelectCurrencyInput } from "../../../components/forms/inputs/SelectCurrencyInput";
import { SelectCountryInput } from "../../../components/forms/inputs/SelectCountryInput";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { Drawer } from "../../../components/Drawer";
import { getErrorMessage } from "../../../error-handler";
import { ActionSpinner } from "../../../components/ActionSpinner";

const mutateMenuSchema = yup.object().shape({
  name: yup.string().required("Enter menu name to continue"),
  countryCode: yup.string().required("Select country to continue"),
  currency: yup.string().required("Select currency to continue"),
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  menu?: Menu;
};

type FormValues = Omit<MenuInfo, "id">;

export const MutateMenuDrawer = ({ isOpen, onClose, menu }: Props) => {
  const { countryCode, currency } = getCountrySpecificValues();

  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const navigate = useNavigate();
  const methods = useForm<FormValues>({
    defaultValues: {
      countryCode,
      currency,
      isMenuOverwritesEnabled: menu?.isMenuOverwritesEnabled || false,
    },
    resolver: yupResolver(mutateMenuSchema),
  });

  const {
    mutateAsync: createMenu,
    isLoading: creationIsLoading,
  } = useCreateMenu();
  const { mutateAsync: updateMenu, isLoading: updateIsLoading } = useUpdateMenu(
    menu?.id
  );

  const { reset } = methods;

  const closeModal = () => {
    methods.reset();
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    try {
      if (menu?.id) {
        const newData: MenuInfo = {
          ...data,
          internalNote: menu.internalNote || "",
        };
        const updateRequest: UpdateMenuData = {
          data: newData,
          organizationKeyName,
          id: menu?.id!,
        };
        await updateMenu(updateRequest);
        toastr.success("Success", "Menu information updated succesfully");
        closeModal();
      } else {
        const request: CreateMenuData = {
          data,
          organizationKeyName,
        };
        const id = await createMenu(request);
        toastr.success("Success", "Menu created succesfully");
        closeModal();
        navigate(routes.menuEdit(organizationKeyName, id));
      }
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  useEffect(() => {
    if (!menu) {
      return;
    }
    reset({
      countryCode: menu.countryAlpha2Code,
      name: menu.name,
      currency: menu.currency,
      isMenuOverwritesEnabled: menu?.isMenuOverwritesEnabled || false,
    });
  }, [menu, reset]);

  return (
    <FormProvider {...methods}>
      <Drawer
        title={`${menu ? "Edit menu information" : "Create menu"}`}
        open={isOpen}
        onClose={closeModal}
        width={500}
        buttons={
          <ButtonAction onClick={methods.handleSubmit(onSubmit)} type="button">
            {creationIsLoading || updateIsLoading ? (
              <ActionSpinner />
            ) : menu?.id ? (
              "Update menu information"
            ) : (
              "Create menu"
            )}
          </ButtonAction>
        }
      >
        <form>
          <FormRow>
            <TextInput
              label="Menu name"
              name="name"
              helperText="Enter name for menu"
            />
          </FormRow>
          {!menu && (
            <FormRow>
              <SelectCountryInput name="countryCode" />
              <SelectCurrencyInput name="currency" />
            </FormRow>
          )}
        </form>
      </Drawer>
    </FormProvider>
  );
};
